import React, { useMemo, useCallback } from 'react'
import { Button, Dropdown, MenuProps, Modal } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import fetchAPI from '../../../../../lib/utils/fetch-api'
import { message } from 'antd'
import { ReturnButtonWrapper } from './styles'

interface Stage {
  id: number
  code: string
  name: string
  style?: {
    color: string
    fontWeight: string
    backgroundColor: string
  }
}

interface SelectedOrder {
  id: number
  stage: string
}

interface ReturnToStageButtonProps {
  selectedOrders: SelectedOrder[]
  stages: Stage[]
  onSuccess?: () => void
}

const ReturnToStageButton: React.FC<ReturnToStageButtonProps> = ({
  selectedOrders,
  stages,
  onSuccess,
}) => {
  const validStages = useMemo(() => stages.filter(stage => stage.code !== 'cancelled'), [stages])

  const minStage = useMemo(() => {
    if (!validStages.length) {
      return null
    }

    const stageIndices = selectedOrders
      .map(order => {
        const stageCode = order.stage
        if (!stageCode) {
          return Infinity
        }
        const stageIndex = validStages.findIndex(stage => stage.code === stageCode)
        if (stageIndex === -1) {
          return Infinity
        }
        return stageIndex
      })
      .filter(index => index !== Infinity)

    if (!stageIndices.length) {
      return null
    }

    return Math.min(...stageIndices)
  }, [selectedOrders, validStages])

  const handleReturnToStage = useCallback(async (stageCode: string) => {
    if (!selectedOrders.length) {
      message.warning('Выберите хотя бы один заказ')
      return
    }

    const ordersToSend = selectedOrders.map(order => order.id)

    try {
      const response = await fetchAPI('/api/logistics/deliveries/return', {
        method: 'POST',
        body: JSON.stringify({
          orders: ordersToSend,
          stage: stageCode,
        }),
      })

      if (response.success) {
        if (stageCode === 'new') {
          Modal.info({
            title: 'Результат возврата на стадию',
            content: (
              <div>
                {response.shifted && response.shifted.length > 0 ? (
                  response.shifted.map(order => (
                    <div key={order.id}>
                      Заказ{' '}
                      <a
                        href={`/logistics/deliveries/${order.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <b>{order.order_reference}</b>
                      </a>
                      : перенесён на очередь {order.newSequence}
                      {response.unapproved?.some(u => u.id === order.id) && (
                        <b> и снята галка о согласовании</b>
                      )}
                    </div>
                  ))
                ) : (
                  <div>Изменений в очередности или согласовании не произошло</div>
                )}
              </div>
            ),
            okText: 'ОК',
            width: 600,
          })
        }
        message.success('Заказы успешно возвращены на стадию')
        onSuccess?.()
      } else {
        throw new Error(response.message || 'Ошибка при возврате')
      }
    } catch (error) {
      message.error('Не удалось вернуть заказы на стадию')
    }
  }, [selectedOrders, onSuccess])

  const menuItems = useMemo(
    () =>
      validStages.map((stage, index) => ({
        key: stage.code,
        disabled: minStage !== null && index >= minStage,
        label: stage.name,
      })),
    [validStages, minStage],
  )

  const menu: MenuProps = useMemo(
    () => ({
      items: menuItems,
      onClick: ({ key }) => {
        if (typeof key === 'string') {
          void handleReturnToStage(key)
        }
      },
    }),
    [menuItems, handleReturnToStage],
  )

  const isDisabled = !selectedOrders.length

  return (
    <ReturnButtonWrapper>
      <Dropdown menu={menu} disabled={isDisabled} trigger={['click']}>
        <Button type="primary" style={{ flex: '1 1' }}>
          Вернуться на стадию <DownOutlined />
        </Button>
      </Dropdown>
    </ReturnButtonWrapper>
  )
}

export default ReturnToStageButton
